<template>
  <component :is="Default">
    <div class="background">
      <Loading />
    </div>
  </component>
</template>

<script setup lang="ts">
import { Loading } from '@/components/loading'
import Default from '~/layouts/default/index.vue'
import { useGateStore } from '~/store/common'
import { useCookies } from '@vueuse/integrations/useCookies'
import { getChkPtnCdDomain } from '~/api/display'
import { GATE_KEYS } from '~/constants/common/gate'
import { CO_CD_INFO } from '~/constants/common'
import { useGoogleTagManagerStore } from '~/store/marketing/google-tag-manager'

const route = useRoute()
const router = useRouter()
const cookies = useCookies()
const gateStore = useGateStore()
const { sendGAPageView } = useGoogleTagManagerStore()

const { getDefaultPtnCd } = gateStore

const state = reactive<{
  redirectionUrl: string
  utmSource: string
  ptnCd: string
}>({
  redirectionUrl: '',
  utmSource: '',
  ptnCd: ''
})

$isLoading.value = true

const init = () => {
  state.ptnCd = (route.query?.ptnCd as string) ?? getDefaultPtnCd()
  state.utmSource = (route.query?.utm_source as string) ?? ''

  const ptnKey = route.query?.ptnKey as string
  if (['630', '631', '632'].includes(ptnKey)) {
    cookies.set(GATE_KEYS.PTN_KEY, ptnKey, {
      expires: new Date(new Date().getTime() + 24 * 60 * 60 * 1000), // 24 hours
      path: '/',
      domain: 'nsmall.com'
    })
  } else {
    cookies.set(GATE_KEYS.PTN_KEY, ptnKey, {
      expires: undefined,
      path: '/',
      domain: 'nsmall.com'
    })
  }

  // NHN 네이버 유입 추적 스크립트의 mac_ad_key(모바일에 전달)
  const macAdKey = route.query?.mac_ad_key
  if (macAdKey) {
    cookies.set('mac_ad_key', macAdKey, {
      expires: undefined,
      path: '/',
      domain: 'nsmall.com'
    })
  }

  // payco 추적키 쿠키 세팅
  const trackingKey = route.query?.trackingKey
  if (trackingKey) {
    cookies.set('trackingKey', trackingKey, {
      expires: undefined,
      path: '/',
      domain: 'nsmall.com'
    })
  }

  // URL 생성
  state.redirectionUrl = (route.query?.target as string) ?? '/'
  if (state.redirectionUrl.startsWith('http')) {
    const u = new URL(state.redirectionUrl)
    state.redirectionUrl = u.pathname
  } else if (!state.redirectionUrl.startsWith('/')) {
    state.redirectionUrl = `/${state.redirectionUrl}`
  }

  /**
   * URL 치환: 3.0 대응
   */
  // const searchParams = new URLSearchParams(state.redirectionUrl.split('?')[1] ?? '')
  if (state.redirectionUrl.startsWith('/product/')) {
    state.redirectionUrl = state.redirectionUrl.replace('product', 'goods')
  } else if (state.redirectionUrl.startsWith('/ProductDisplay')) {
    if (route.query?.partNumber !== 'undefined' && route.query?.partNumber !== null) {
      //query string에 partNumber가 2개가 있을 수있어서 replace 하지않고 goodsCd를 넣기
      if (typeof route.query?.partNumber === 'string') {
        state.redirectionUrl = `/goods/${route.query?.partNumber as string}`
      } else {
        state.redirectionUrl = `/goods/${route.query?.partNumber[0] as string}`
      }
    }
  } else if (state.redirectionUrl.startsWith('/search/result/')) {
    state.redirectionUrl = state.redirectionUrl.replace('/result', '')
  } else if (state.redirectionUrl.startsWith('/event/application')) {
    state.redirectionUrl = '/'
  } else if (state.redirectionUrl.startsWith('/event/lucky-box')) {
    state.redirectionUrl = `/store${state.redirectionUrl}`
  }

  /**
   * short URL 대응
   */
  if (state.redirectionUrl === '/goods') {
    // https://m.nsmall.com/store/goods/{goodsCd}
    const goodsCd = route.query?.goodsCd as string
    state.redirectionUrl += `/${goodsCd}`
  } else if (state.redirectionUrl === '/customer-center/counsel-survey') {
    // https://m.nsmall.com/customer-center/counsel-survey/{custNum)/{curDate}/{loginNm}/{loginld}/{appltype}/{cnnlCd}/{reltNum}/{custCmplnNum}
    const pathParams = []
    pathParams.push(route.query?.custNum as string)
    pathParams.push(route.query?.curDate as string)
    pathParams.push(route.query?.loginNm as string)
    pathParams.push(route.query?.loginld as string)
    pathParams.push(route.query?.appltype as string)
    pathParams.push(route.query?.cnnlCd as string)
    pathParams.push(route.query?.reltNum as string)
    pathParams.push(route.query?.custCmplnNum as string)
    state.redirectionUrl += `/${pathParams.join('/')}`
  }

  // 쿼리스트링 조합
  const urlParts = new URL(state.redirectionUrl, window.location.origin)

  if (route.query?.multiCd) {
    urlParts.searchParams.append('multiCd', route.query?.multiCd as string)
  }

  if (route.query?.redirectYn) {
    urlParts.searchParams.append('redirectYn', route.query?.redirectYn as string)
  }

  if (route.query?.ptnCd) {
    //ptnCd url에 노출하여 문제 URL 빠르게 파악하기 위해 추가 -khkim82
    urlParts.searchParams.append('ptnCd', route.query?.ptnCd as string)
  }

  if (Number(state.ptnCd) === 0 || isNaN(Number(state.ptnCd))) {
    state.ptnCd = CO_CD_INFO.BASE_WEB
  }

  // 134 : 네이버 쇼핑검색광고
  if ('pla' === route.query?.nv_ad) {
    state.ptnCd = '134'
  }

  // 683
  if (state.ptnCd === '452' && route.query?.gclid) {
    state.ptnCd = '683'
  }

  // NSSR-42234 홈빠 인입시 홈빠 회원번호 셋팅
  let ptnCustNum = route.query?.userNo
  if (['693', '694'].includes(state.ptnCd)) {
    cookies.set('ptnCustNum', ptnCustNum, {
      expires: undefined,
      path: '/',
      domain: 'nsmall.com'
    })
  }

  // 위메프 원더쇼핑일 때 coCd 파라미터 추가
  if (state.ptnCd === '985') {
    urlParts.searchParams.append('wonderY', state.ptnCd)
  }

  sendGAPageView('GATE')
  setGA360Params(urlParts.searchParams)
  state.redirectionUrl = urlParts.href.replace(urlParts.origin, '')
}

const setGA360Params = (searchParams: URLSearchParams) => {
  if (document.referrer) {
    searchParams.append('referrer', document.referrer)
  }
  if (route.query?.utm_campaign) {
    searchParams.append('utm_campaign', route.query?.utm_campaign as string)
  }
  if (route.query?.utm_content) {
    searchParams.append('utm_content', route.query?.utm_content as string)
  }
  if (route.query?.utm_term) {
    searchParams.append('utm_term', route.query?.utm_term as string)
  }

  if (state.ptnCd === '134') {
    searchParams.append('utm_source', 'naver_shop_searchad')
    searchParams.append('utm_medium', 'display')
    state.utmSource = 'naver_shop_searchad'
  } else {
    if (route.query?.utm_source) {
      searchParams.append('utm_source', route.query?.utm_source as string)
    }
    if (route.query?.utm_medium) {
      searchParams.append('utm_medium', route.query?.utm_medium as string)
    }
  }
}

const process = () => {
  if (['630', '631', '632'].includes(state.ptnCd)) {
    cookies.set(GATE_KEYS.PTN_CD, state.ptnCd, {
      expires: new Date(new Date().getTime() + 24 * 60 * 60 * 1000), // 24 hours
      path: '/',
      domain: 'nsmall.com'
    })
    cookies.set(GATE_KEYS.PTN_LINK, 'Y', {
      expires: undefined,
      path: '/',
      domain: 'nsmall.com'
    })
  } else {
    cookies.set(GATE_KEYS.PTN_CD, state.ptnCd, {
      expires: new Date(new Date().getTime() + 3 * 60 * 60 * 1000), // 3시간
      path: '/',
      domain: 'nsmall.com'
    })
  }

  if (state.utmSource) {
    cookies.set('utm_source', state.utmSource, {
      expires: undefined,
      path: '/',
      domain: 'nsmall.com'
    })
  }

  router.replace(state.redirectionUrl)
}

const checkPtnCd = () => {
  if (state.ptnCd === CO_CD_INFO.BASE_WEB) {
    process()
  } else {
    const payload = {
      ptnCd: state.ptnCd
    }
    getChkPtnCdDomain(payload)
      .then(res => {
        if (res.data.resultCode !== '0000') {
          return
        }

        if ('Y' !== res.data.resultData?.useYn) {
          state.ptnCd = getDefaultPtnCd()
        }

        // if ('Y' === res.data.resultData?.useYn) {
        //   const chkUrl = res.data.resultData?.chkUrl
        //   if (chkUrl && (!document.referrer || !document.referrer.includes(chkUrl))) {
        //     state.ptnCd = CO_CD_INFO.BASE_WEB
        //   }
        //   state.ptnCd = CO_CD_INFO.BASE_WEB
        // } else {
        //   state.ptnCd = CO_CD_INFO.BASE_WEB
        // }
      })
      .catch(_error => {})
      .finally(() => {
        process()
      })
  }
}

onBeforeMount(() => {
  init()
})

onMounted(() => {
  checkPtnCd()
})

onUnmounted(() => {
  $isLoading.value = false
})
</script>

<style scoped>
.background {
  height: 100vh;
}
</style>
